import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import type { QuoteResponseDto, QuotesControllerListQuotesParams } from '@trellis/api'
import { Button } from '@trellis/roots/button'
import { Checkbox } from '@trellis/roots/checkbox'
import { BulkActionBar, ColumnVisibilityMenu, DataTable } from '@trellis/roots/data'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { SearchInput } from '@trellis/roots/forms'
import { Label } from '@trellis/roots/label'
import { PageHeader } from '@trellis/roots/navigation'
import { ConfirmDialog } from '@trellis/roots/overlays'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { StatusBadge } from '@trellis/roots/status'
import { BulkTagPicker, TagSelector } from '@trellis/roots/tags'
import { FEATURE } from '@trellis/shared'
import {
  Archive,
  ChevronLeft,
  ChevronRight,
  Download,
  MoreHorizontal,
  Plus,
  Tag,
  Trash2,
  Upload,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { FilterSheet } from '@/components/shell'
import {
  useArchiveQuoteMutation,
  useAssignQuoteTagsMutation,
  useBulkArchive,
  useBulkDelete,
  useBulkTagApply,
  useColumnVisibility,
  useDebouncedSearch,
  useDeleteQuoteMutation,
  useDisclosure,
  usePinnedColumn,
  useQuoteRowActions,
  useQuotesQuery,
  useRemoveQuoteTagMutation,
  useTableSorting,
  useTagsQuery,
} from '@/hooks'
import { useIsFeatureEnabled } from '@/hooks/useFeatureFlags'
import { useSetBreadcrumbs } from '@/lib/breadcrumbs'
import { formatCalendarDate, formatCurrency, formatDate } from '@/lib/format'
import { buildTagsColumn, filterGatedColumns } from '@/lib/table-columns'
import { cn } from '@/lib/utils'
import { exportQuotes } from '@/requests/data-export'

import {
  QuoteActionDialogs,
  QuotePreviewSheet,
  QuoteRowActionsMenu,
} from './(components)'

type QuoteStatus = QuoteResponseDto['status']

const quotesRouteApi = getRouteApi('/_authenticated/_dashboard/quotes/')

const COLUMN_SPECS = [
  { id: 'displayNumber', label: 'Quote #' },
  { id: 'clientName', label: 'Client' },
  { id: 'status', label: 'Status' },
  { id: 'total', label: 'Total' },
  { id: 'expiresAt', label: 'Expires' },
  { id: 'createdAt', label: 'Created' },
  { id: 'tags', label: 'Tags' },
]

export const ALL_QUOTE_STATUSES = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'DECLINED',
  'EXPIRED',
  'SUPERSEDED',
  'INVOICED',
] as const satisfies readonly QuoteStatus[]

const ALL_STATUSES = ALL_QUOTE_STATUSES

interface QuotesQuerySearchParams {
  status?: QuoteStatus
  archived: boolean
}

/**
 * Turns the quotes route's validated search into the same options shape passed to
 * {@link useQuotesQuery} here and to `quotesInfiniteQueryOptions` in the route's loader —
 * keeping both on the same query key so the loader's prefetch is actually hit. `tags` and
 * `q` are intentionally excluded: they're local component state, not URL-synced.
 */
export function buildQuotesQueryOptions(
  search: QuotesQuerySearchParams,
): QuotesControllerListQuotesParams {
  return {
    status: search.status ? [search.status] : undefined,
    limit: 20,
    archived: search.archived,
  }
}

const STATUS_LABELS: Record<QuoteStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  ACCEPTED: 'Accepted',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  SUPERSEDED: 'Superseded',
  INVOICED: 'Invoiced',
}

const FILTERS_STORAGE_KEY = 'quotes-filters'

function buildQuoteColumns(
  onDeleteClick: (quote: QuoteResponseDto) => void,
  onPreviewClick: (quote: QuoteResponseDto) => void,
  onArchiveClick: (quote: QuoteResponseDto) => void,
  onUnarchiveClick: (quote: QuoteResponseDto) => void,
  onMarkSentClick: (quote: QuoteResponseDto) => void,
  isTagsEnabled: boolean,
): ColumnDef<QuoteResponseDto>[] {
  return [
    { accessorKey: 'displayNumber', header: 'Quote #' },
    { accessorKey: 'clientName', header: 'Client', meta: { truncate: true } },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'total',
      header: 'Total',
      cell: ({ row }) => formatCurrency(row.original.total),
    },
    {
      accessorKey: 'expiresAt',
      header: 'Expires',
      cell: ({ row }) =>
        row.original.expiresAt ? formatCalendarDate(row.original.expiresAt) : '—',
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    ...(isTagsEnabled ? [buildTagsColumn<QuoteResponseDto>()] : []),
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      meta: { align: 'right' },
      cell: ({ row }) => (
        <QuoteRowActionsMenu
          quote={row.original}
          onDeleteClick={onDeleteClick}
          onPreviewClick={onPreviewClick}
          onArchiveClick={onArchiveClick}
          onUnarchiveClick={onUnarchiveClick}
          onMarkSentClick={onMarkSentClick}
        />
      ),
    },
  ]
}

function readFiltersFromStorage(): { status?: QuoteStatus } {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY)
    if (!stored) return {}
    const parsed = JSON.parse(stored) as { status?: QuoteStatus }
    return { status: parsed.status }
  } catch {
    return {}
  }
}

function writeFiltersToStorage(status: QuoteStatus | undefined) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ status }))
  } catch {
    // localStorage may be unavailable in some environments; silently ignore
  }
}

export function QuotesPage() {
  useSetBreadcrumbs([{ label: 'Quotes' }])
  const { status: statusFilter, archived: showArchived } = quotesRouteApi.useSearch()
  const navigateSearch = quotesRouteApi.useNavigate()
  const quotePreview = useDisclosure<string>()
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const {
    inputValue: searchInput,
    debouncedValue: searchQuery,
    setInputValue: setSearchInput,
  } = useDebouncedSearch()
  const { columnVisibility, onColumnVisibilityChange } =
    useColumnVisibility('quotes-columns')
  const { sorting, onSortingChange } = useTableSorting('quotes-sorting')
  const { pinnedColumnId, onPinnedColumnChange } = usePinnedColumn('quotes-pinned-column')

  // Restore last filter state from localStorage on first render if URL has no params
  const hasRestoredStoredFilters = useRef(false)
  useEffect(() => {
    if (hasRestoredStoredFilters.current) return
    hasRestoredStoredFilters.current = true
    if (statusFilter !== undefined) return
    const stored = readFiltersFromStorage()
    if (!stored.status) return
    void navigateSearch({ search: (prev) => ({ ...prev, status: stored.status }) })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- run-once on mount; ref guard prevents re-runs

  const navigate = useNavigate()
  const { isEnabled: isTagsEnabled } = useIsFeatureEnabled(FEATURE.TAGS)
  const allTags = useTagsQuery()

  const {
    isFetching,
    pageIndex,
    setPageIndex,
    currentPageData,
    totalPages,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
  } = useQuotesQuery({
    ...buildQuotesQueryOptions({ status: statusFilter, archived: showArchived }),
    ...(tagFilter.length > 0 ? { tags: tagFilter } : {}),
    ...(searchQuery ? { q: searchQuery } : {}),
  })

  const deleteMutation = useDeleteQuoteMutation()
  const assignQuoteTagsMutation = useAssignQuoteTagsMutation()
  const removeQuoteTagMutation = useRemoveQuoteTagMutation()
  const archiveMutation = useArchiveQuoteMutation()
  const quoteActions = useQuoteRowActions()

  const rows = currentPageData?.data ?? []
  const selectedQuotes = rows.filter((row) => rowSelection[row.id])

  function clearSelection() {
    setRowSelection({})
  }

  function deselectIds(ids: string[]) {
    setRowSelection((current) => {
      const next = { ...current }
      for (const id of ids) delete next[id]
      return next
    })
  }

  useEffect(() => {
    setPageIndex(0)
  }, [statusFilter, tagFilter, searchQuery, showArchived, setPageIndex])

  const quoteColumns = buildQuoteColumns(
    quoteActions.setQuoteToDelete,
    (quote) => quotePreview.open(quote.id),
    quoteActions.handleArchive,
    quoteActions.handleUnarchive,
    quoteActions.setMarkSentQuote,
    isTagsEnabled,
  )
  const columnSpecs = filterGatedColumns(COLUMN_SPECS, isTagsEnabled)

  useEffect(() => {
    writeFiltersToStorage(statusFilter)
  }, [statusFilter])

  function handleStatusChange(status: QuoteStatus | undefined) {
    void navigateSearch({ search: (prev) => ({ ...prev, status }) })
  }

  function handleArchivedChange(archived: boolean) {
    void navigateSearch({ search: (prev) => ({ ...prev, archived }) })
  }

  const {
    open: bulkDeleteOpen,
    setOpen: setBulkDeleteOpen,
    handleBulkDelete,
  } = useBulkDelete({
    selected: selectedQuotes,
    getId: (quote) => quote.id,
    deleteFn: (id) => deleteMutation.mutateAsync(id),
    label: 'quote',
    onSuccess: clearSelection,
  })

  const {
    open: bulkTagPickerOpen,
    setOpen: setBulkTagPickerOpen,
    handleBulkTagApply,
    allSelectedTagIds,
    someSelectedTagIds,
  } = useBulkTagApply({
    selected: selectedQuotes,
    getId: (quote) => quote.id,
    getTags: (quote) => quote.tags ?? [],
    assignFn: (id, tagIds) =>
      assignQuoteTagsMutation.mutateAsync({ quoteId: id, tagIds }),
    removeFn: (id, tagId) => removeQuoteTagMutation.mutateAsync({ quoteId: id, tagId }),
    invalidateKey: ['quotes'],
    onSuccess: clearSelection,
  })

  const { handleBulkArchive } = useBulkArchive({
    selected: selectedQuotes,
    getId: (quote) => quote.id,
    archiveFn: (id) => archiveMutation.mutateAsync(id),
    label: 'quote',
    onSuccess: deselectIds,
  })

  return (
    <div className="p-8">
      <PageHeader
        title="Quotes"
        subtitle="Send quotes and track client responses."
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="More options"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => exportQuotes()}>
                  <Download className="mr-2 size-4" />
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Import</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/quotes/import">
                    <Upload className="mr-2 size-4" />
                    Import CSV
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button asChild>
              <Link to="/quotes/new">
                <Plus className="size-4" />
                New Quote
              </Link>
            </Button>
          </>
        }
      />

      {/* Toolbar / Bulk action bar */}
      {selectedQuotes.length > 0 ? (
        <BulkActionBar
          selectedCount={selectedQuotes.length}
          actions={[
            ...(isTagsEnabled
              ? [
                  {
                    label: 'Assign tags',
                    icon: <Tag className="size-4" />,
                    onClick: () => setBulkTagPickerOpen(true),
                  },
                ]
              : []),
            {
              label: 'Archive',
              icon: <Archive className="size-4" />,
              onClick: () => void handleBulkArchive(),
            },
            {
              label: 'Delete',
              icon: <Trash2 className="size-4" />,
              onClick: () => setBulkDeleteOpen(true),
              variant: 'destructive',
            },
          ]}
          onClearSelection={clearSelection}
          className="mb-3"
        />
      ) : (
        <div className="mb-3">
          <FilterSheet
            activeCount={
              (statusFilter !== undefined ? 1 : 0) +
              (tagFilter.length > 0 ? 1 : 0) +
              (showArchived ? 1 : 0)
            }
            onClear={() => {
              handleStatusChange(undefined)
              setTagFilter([])
              handleArchivedChange(false)
            }}
            toolbar={
              <>
                <Select
                  value={statusFilter ?? 'all'}
                  onValueChange={(value) =>
                    handleStatusChange(
                      value === 'all' ? undefined : (value as QuoteStatus),
                    )
                  }
                >
                  <SelectTrigger
                    className={cn(
                      'h-9 w-[160px] rounded-lg font-normal',
                      !statusFilter && 'text-muted-foreground',
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {ALL_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {STATUS_LABELS[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isTagsEnabled && (
                  <TagSelector
                    tags={allTags.data ?? []}
                    value={tagFilter}
                    onChange={setTagFilter}
                    variant="filter"
                  />
                )}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="quotes-show-archived"
                    checked={showArchived}
                    onCheckedChange={(checked) => handleArchivedChange(checked === true)}
                  />
                  <Label
                    htmlFor="quotes-show-archived"
                    className="text-muted-foreground text-sm font-normal"
                  >
                    Show archived
                  </Label>
                </div>
                <div className="flex-1" />
                <SearchInput
                  value={searchInput}
                  onChange={(changeEvent) => setSearchInput(changeEvent.target.value)}
                  placeholder="Search quotes…"
                  className="max-w-xs"
                />
                <ColumnVisibilityMenu
                  columns={columnSpecs}
                  columnVisibility={columnVisibility}
                  onColumnVisibilityChange={onColumnVisibilityChange}
                />
              </>
            }
            sheet={
              <>
                <Select
                  value={statusFilter ?? 'all'}
                  onValueChange={(value) =>
                    handleStatusChange(
                      value === 'all' ? undefined : (value as QuoteStatus),
                    )
                  }
                >
                  <SelectTrigger
                    className={cn(
                      'h-9 w-full rounded-lg font-normal',
                      !statusFilter && 'text-muted-foreground',
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {ALL_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {STATUS_LABELS[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isTagsEnabled && (
                  <TagSelector
                    tags={allTags.data ?? []}
                    value={tagFilter}
                    onChange={setTagFilter}
                    variant="filter"
                  />
                )}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="quotes-show-archived-sheet"
                    checked={showArchived}
                    onCheckedChange={(checked) => handleArchivedChange(checked === true)}
                  />
                  <Label
                    htmlFor="quotes-show-archived-sheet"
                    className="text-muted-foreground text-sm font-normal"
                  >
                    Show archived
                  </Label>
                </div>
              </>
            }
          />
        </div>
      )}

      <DataTable
        columns={quoteColumns}
        data={rows}
        isLoading={isFetching && rows.length === 0}
        emptyMessage="No quotes found."
        className="rounded-b-none"
        onRowClick={(quote) =>
          void navigate({ to: '/quotes/$id', params: { id: quote.id } })
        }
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        sorting={sorting}
        onSortingChange={onSortingChange}
        enableColumnPinning
        pinnedColumnId={pinnedColumnId}
        onPinnedColumnChange={onPinnedColumnChange}
        enableSelection={true}
        getRowId={(row) => row.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
      />

      {/* Pagination footer */}
      <div className="bg-muted/30 text-muted-foreground mt-0 flex items-center justify-between rounded-b-lg border-x border-b px-4 py-3 text-[12.5px]">
        <span>
          Page {pageIndex + 1} of {totalPages}
        </span>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={pageIndex === 0}
            onClick={goToPrevPage}
            className="h-7 text-xs"
          >
            <ChevronLeft className="mr-1 size-3.5" />
            Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasNextPage || isFetching}
            onClick={goToNextPage}
            className="h-7 text-xs"
          >
            Next
            <ChevronRight className="ml-1 size-3.5" />
          </Button>
        </div>
      </div>

      <QuotePreviewSheet
        quoteId={quotePreview.value}
        onOpenChange={quotePreview.onOpenChange}
      />

      <QuoteActionDialogs
        quoteToDelete={quoteActions.quoteToDelete}
        onDeleteOpenChange={(isOpen) => {
          if (!isOpen) quoteActions.setQuoteToDelete(null)
        }}
        onConfirmDelete={quoteActions.handleConfirmDelete}
        isDeletePending={quoteActions.isDeletePending}
        markSentQuote={quoteActions.markSentQuote}
        onMarkSentOpenChange={(isOpen) => {
          if (!isOpen) quoteActions.setMarkSentQuote(null)
        }}
        onConfirmMarkSent={quoteActions.handleConfirmMarkSent}
        isMarkSentPending={quoteActions.isMarkSentPending}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selectedQuotes.length} quote${selectedQuotes.length === 1 ? '' : 's'}?`}
        description={`This will permanently delete ${selectedQuotes.length} quote${selectedQuotes.length === 1 ? '' : 's'}. This action cannot be undone.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
        onConfirm={() => void handleBulkDelete()}
      />

      {isTagsEnabled && (
        <BulkTagPicker
          open={bulkTagPickerOpen}
          onOpenChange={setBulkTagPickerOpen}
          allTags={allTags.data ?? []}
          allSelectedTagIds={allSelectedTagIds}
          someSelectedTagIds={someSelectedTagIds}
          onApply={(toAdd, toRemove) => void handleBulkTagApply(toAdd, toRemove)}
          isApplying={
            assignQuoteTagsMutation.isPending || removeQuoteTagMutation.isPending
          }
        />
      )}
    </div>
  )
}
